import React, {useCallback, useEffect, useState} from "react";
import {delay, getFontWeight} from "./ShotViewport";
import {Narration} from "../../../feature/project/Shot";
import {ConfigNarration} from "../../../feature/project/project";
import {useMovable} from "react-move-hook";
import {Position2D} from "react-move-hook/lib/types/util";
import {NumberSize, Resizable} from "re-resizable";
import {useAppDispatch, useAppSelector} from "../../../feature/tools";
import {saveNarration} from "../../../feature/project/narrationSlice";
import {useParams} from "react-router-dom";
import {Typewriter} from "../../../typography/Typewriter";

export const NarrationElement = (props: {
    editable: boolean,
    narration: Narration,
    configNarration: ConfigNarration,
    isSelected: boolean,
    scale: number,
    onElementSelected: (narration: Narration) => void,
    isPlaying: boolean,
    onPlayEnd: (type: string) => void
}) => {
    const dispatch = useAppDispatch()
    const {projectId, shotId} = useParams()
    const [show, setShow] = useState(false)
    const [resizing, setResizing] = useState<NumberSize | undefined>(undefined)
    const [size, setSize] = useState<NumberSize>({
        width: props.narration.width ?? props.configNarration.width,
        height: props.narration.height ?? props.configNarration.height
    })
    const [state, setState] = useState<{
        moving: boolean,
        changed: boolean,
        position: Position2D,
        delta: Position2D | undefined
    }>({
        moving: false,
        changed: false,
        position: {
            x: props.narration.x ?? props.configNarration.x,
            y: props.narration.y ?? props.configNarration.y
        },
        delta: undefined,
    });
    const shot = useAppSelector(state => state.shots.shot)

    useEffect(() => {
        setSize({
            width: props.narration.width ?? props.configNarration.width,
            height: props.narration.height ?? props.configNarration.height
        })
        setState({
            moving: false,
            changed: false,
            position: {
                x: props.narration.x ?? props.configNarration.x,
                y: props.narration.y ?? props.configNarration.y
            },
            delta: undefined,
        })
    }, [props.narration])

    useEffect(() => {
        if (props.isPlaying && show) {
            setShow(false)
        }
    }, [props.isPlaying])

    useEffect(() => {
        if (projectId && shotId && parseInt(shotId) == shot?.id) {
            dispatch(saveNarration({
                projectId: parseInt(projectId),
                shotId: parseInt(shotId),
                narration: {
                    ...props.narration,
                    x: state.position.x,
                    y: state.position.y,
                    width: size.width,
                    height: size.height
                }
            }))
        }
    }, [state.changed])

    const handleChange = useCallback((moveData: any) => {
        if (!props.editable) return
        setState((state) => ({
            moving: moveData.moving,
            changed: moveData.stoppedMoving && (moveData.delta.x !== 0 || moveData.delta.y !== 0),
            position: moveData.stoppedMoving
                ? {
                    x: state.position.x + moveData.delta.x,
                    y: state.position.y + moveData.delta.y,
                }
                : state.position,
            delta: moveData.moving ? moveData.delta : undefined,
        }));
    }, [state.position]);

    const ref = useMovable({onChange: handleChange});

    useEffect(() => {
        document.body.classList.toggle("moving", state.moving);
    }, [state.moving]);

    useEffect(() => {
        if (show) return
        delay(props.narration.delay ?? 0).then(() => {
            setShow(true)
            if (props.isPlaying) props.onPlayEnd('Narrative')
            //TODO end playing when stop typing
        })
    }, [show])

    useEffect(() => {
        if (!props.isPlaying) {
            setShow(true)
        }
    }, [props.isPlaying])

    return (
        <>
            {
                show ?
                    <div
                        ref={!resizing ? ref : null}
                        style={{
                            position: 'absolute',
                            top: `${state.position.y}px`,
                            left: `${state.position.x}px`,
                            outline: props.isSelected ? `${2 / (props.scale)}px solid #1890ff` : undefined,
                            borderImageSource: `url(${props.configNarration.background_url})`,
                            borderImageSlice: `${props.configNarration.background_nine_slice_top} ${props.configNarration.background_nine_slice_right} ${props.configNarration.background_nine_slice_bottom} ${props.configNarration.background_nine_slice_left} fill`,
                            borderImageWidth: `${props.configNarration.background_nine_slice_top}px ${props.configNarration.background_nine_slice_right}px ${props.configNarration.background_nine_slice_bottom}px ${props.configNarration.background_nine_slice_left}px`,
                            zIndex: (props.narration.z ?? props.configNarration.z),
                            width: `${size.width}px`,
                            height: `${size.height}px`,
                            transformOrigin: 'top left',
                            cursor: 'pointer',
                            transform: (state.delta ? `translate3d(${state.delta.x}px, ${state.delta.y}px, 0)` : ''),
                        }}
                        onMouseDown={(e) => {
                            e.stopPropagation()
                            if (props.editable) {
                                props.onElementSelected({...props.narration, type: 'Narrative'})
                            }
                        }}
                        onClick={(e) => {
                            e.stopPropagation()
                        }}
                    >
                        {props.isSelected ? <>
                            <div style={{
                                width: 32,
                                height: 32,
                                backgroundColor: '#4583ff',
                                borderRadius: 16,
                                position: "absolute",
                                left: -16,
                                top: -16
                            }}/>
                            <div style={{
                                width: 32,
                                height: 32,
                                backgroundColor: '#4583ff',
                                borderRadius: 16,
                                position: "absolute",
                                left: -16,
                                bottom: -16
                            }}/>
                            <div style={{
                                width: 32,
                                height: 32,
                                backgroundColor: '#4583ff',
                                borderRadius: 16,
                                position: "absolute",
                                right: -16,
                                top: -16
                            }}/>
                            <div style={{
                                width: 32,
                                height: 32,
                                backgroundColor: '#4583ff',
                                borderRadius: 16,
                                position: "absolute",
                                right: -16,
                                bottom: -16
                            }}/>
                            <div style={{
                                width: 32,
                                height: 32,
                                backgroundColor: '#4583ff',
                                borderRadius: 16,
                                position: "absolute",
                                right: 'calc(50% - 16px)',
                                bottom: -16
                            }}/>
                            <div style={{
                                width: 32,
                                height: 32,
                                backgroundColor: '#4583ff',
                                borderRadius: 16,
                                position: "absolute",
                                right: 'calc(50% - 16px)',
                                top: -16
                            }}/>
                            <div style={{
                                width: 32,
                                height: 32,
                                backgroundColor: '#4583ff',
                                borderRadius: 16,
                                position: "absolute",
                                top: 'calc(50% - 16px)',
                                right: -16
                            }}/>
                            <div style={{
                                width: 32,
                                height: 32,
                                backgroundColor: '#4583ff',
                                borderRadius: 16,
                                position: "absolute",
                                top: 'calc(50% - 16px)',
                                left: -16
                            }}/>
                        </> : ''}
                        <Resizable
                            handleStyles={{
                                top: {cursor: "ns-resize"},
                                bottom: {cursor: "ns-resize"},
                                left: {cursor: "ew-resize"},
                                right: {cursor: "ew-resize"},
                                topLeft: {cursor: "nwse-resize"},
                                bottomRight: {cursor: "nwse-resize"},
                                topRight: {cursor: "nesw-resize"},
                                bottomLeft: {cursor: "nesw-resize"},
                            }}
                            size={{width: size.width, height: size.height}}
                            onResizeStart={(e) =>{
                                if (!props.editable) return
                                setResizing({width: size.width, height: size.height})
                            }}
                            onResize={(e, direction, ref, d) => {
                                if (!props.editable) return
                                if (direction === 'topLeft') {
                                    setState({
                                        ...state,
                                        delta: {
                                            x: -d.width,
                                            y: -d.height,
                                        }
                                    })
                                } else if (direction === 'left' || direction === 'bottomLeft') {
                                    setState({
                                        ...state,
                                        delta: {
                                            x: -d.width,
                                            y: state.delta?.y ?? 0
                                        }
                                    })
                                } else if (direction === 'top' || direction === 'topRight') {
                                    setState({
                                        ...state,
                                        delta: {
                                            x: state.delta?.x ?? 0,
                                            y: -d.height,
                                        }
                                    })
                                }
                                setSize({
                                    width: (resizing?.width ?? 0) + d.width,
                                    height: (resizing?.height ?? 0) + d.height,
                                });

                            }}
                            onResizeStop={(e, direction, ref, d) => {
                                if (!props.editable) return
                                setResizing(undefined)
                                if (projectId && shotId) {
                                    console.log("ResizeStop")

                                    dispatch(saveNarration({
                                        projectId: parseInt(projectId),
                                        shotId: parseInt(shotId),
                                        narration: {
                                            ...props.narration,
                                            x: state.position.x + (state.delta?.x ?? 0),
                                            y: state.position.y + (state.delta?.y ?? 0),
                                            width: size.width,
                                            height: size.height
                                        }
                                    }))
                                }
                                setState({
                                    ...state,
                                    position: {
                                        x: state.position.x + (state.delta?.x ?? 0),
                                        y: state.position.y + (state.delta?.y ?? 0),
                                    },
                                    delta: undefined
                                })
                            }}
                        >
                            {props.narration.narrator?.icon ?
                                <img
                                    draggable={false}
                                    style={{
                                        position: 'absolute',
                                        width: props.configNarration.avatar_width,
                                        height: props.configNarration.avatar_height,
                                        left: `${props.configNarration.avatar_x}px`,
                                        top: `${props.configNarration.avatar_y}px`,
                                    }}
                                    src={props.narration.narrator.icon}/>
                                : ''}
                            {props.narration.narrator ?
                                <div
                                    style={{
                                        fontSize: `${props.configNarration.name_font_size}px`,
                                        color: `${props.narration.narrator?.color ?? props.configNarration.name_font_color}`,
                                        fontStyle: `${props.configNarration.name_font_style}`,
                                        fontWeight: getFontWeight(props.configNarration.name_font_weight),
                                        position: 'absolute',
                                        top: `${props.configNarration.name_y}px`,
                                        left: `${(props.configNarration.name_x ?? 0) - (props.narration.narrator.icon ? 0 : ((props.configNarration.avatar_width ?? 0) + (props.configNarration.avatar_x ?? 0)))}px`,
                                    }}>{props.narration.narrator.title}</div>
                                : ''}
                            <div
                                style={{
                                    fontSize: `${props.configNarration.text_font_size}px`,
                                    lineHeight: `${props.configNarration.text_font_size}px`,
                                    position: 'absolute',
                                    color: `${props.configNarration.text_font_color}`,
                                    fontStyle: `${props.configNarration.text_font_style}`,
                                    fontWeight: getFontWeight(props.configNarration.text_font_weight),
                                    top: `${(props.configNarration.text_y ?? 0) - (props.narration.narrator ? 0 : (props.configNarration.name_font_size ?? 0))}px`,
                                    left: `${(props.configNarration.text_x ?? 0) - (props.narration.narrator?.icon ? 0 : ((props.configNarration.avatar_width ?? 0) + (props.configNarration.avatar_x ?? 0)))}px`,
                                    width: `${size.width - (props.narration.narrator ? (props.configNarration.width - (props.configNarration.text_width ?? 0)) : ((props.configNarration.text_x ?? 0) - (props.configNarration.avatar_width ?? 0)) )}px`,
                                    whiteSpace: 'pre-line'
                                }}>
                                <Typewriter
                                    text={props.narration.text ?? ''}
                                    type={props.configNarration.text_appearing ?? 'All'}
                                />
                            </div>

                        </Resizable>
                    </div>
                    : ''
            }
        </>
    )
}